import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@mui/material';

import {
  Features,
  FitBarConfig,
  ProductRecommendationsConfig,
  VirtualTryOnConfig,
  isNotNullOrUndefined,
  getWidgetCdnPath
} from '../framework';
import {
  FormFieldWrapper,
  FormLabel,
  FormRow,
  FormSection,
  FormSectionTitle,
  FormSubsection
} from './form-input';

import { ButtonPositionEditor } from './button-position-editor';
import {
  SwitchField,
  TextField,
  SelectField,
  NumberField,
  BackgroundImageField,
  WatermarkImageField
} from './fields';

export type FeatureSet = 'widget' | 'portal' | 'all';

export interface FeatureEditorProps {
  features: Features;
  onChange: (features: Features) => unknown;
  featureSet?: FeatureSet;
  hideTitle?: boolean;
  externallyChanged?: Partial<Features>;
  retailerSlug?: string;
}

export function FeatureEditor({
  features,
  onChange,
  featureSet = 'all',
  hideTitle,
  externallyChanged,
  retailerSlug = ''
}: FeatureEditorProps) {
  const { t } = useTranslation();

  const updateProductRecommendations = useCallback(
    (field: keyof ProductRecommendationsConfig, value: boolean) => {
      onChange({
        ...features,
        productRecommendations: {
          enabled: features?.productRecommendations?.enabled ?? false,
          matchColor: features?.productRecommendations?.matchColor ?? true,
          matchGender: features?.productRecommendations?.matchGender ?? true,
          matchCustomLabel0:
            features?.productRecommendations?.matchCustomLabel0 ?? false,
          matchProductCategory:
            features?.productRecommendations?.matchProductCategory ?? false,
          onlyOutOfStock:
            features?.productRecommendations?.onlyOutOfStock ?? false,
          [field]: value
        }
      });
    },
    [features, onChange]
  );

  const updateFitBar = useCallback(
    (field: keyof FitBarConfig, value: boolean | number) => {
      onChange({
        ...features,
        fitBar: {
          enabled: features?.fitBar?.enabled ?? false,
          onlyWhenNotFilled: features?.fitBar?.onlyWhenNotFilled ?? false,
          hideIfZero: features?.fitBar?.hideIfZero ?? false,
          showIfUndefined: features?.fitBar?.showIfUndefined ?? false,
          sizeChartReturnsThreshold:
            features?.fitBar?.sizeChartReturnsThreshold ?? 0,
          customFitBarVisuals: features?.fitBar?.customFitBarVisuals ?? false,
          usePerProductTtsIfSet:
            features?.fitBar?.usePerProductTtsIfSet ?? false,
          [field]: value
        }
      });
    },
    [features, onChange]
  );
  const updateVirtualTryOn = useCallback(
    (
      field: keyof VirtualTryOnConfig,
      value: boolean | number | string | object
    ) => {
      onChange({
        ...features,
        virtualTryOn: {
          enabled: features?.virtualTryOn?.enabled ?? false,
          customPdpButton: features?.virtualTryOn?.customPdpButton ?? false,
          pdpImageTakeover: features?.virtualTryOn?.pdpImageTakeover ?? false,
          model: 'v1',
          modelName: features?.virtualTryOn?.modelName ?? 'tryon-v1.6',
          mode: features?.virtualTryOn?.mode ?? 'balanced',
          limitMonthly: features?.virtualTryOn?.limitMonthly ?? 1000,
          targetDevice: features?.virtualTryOn?.targetDevice ?? 'all',
          onlyOnSelectedProducts:
            features?.virtualTryOn?.onlyOnSelectedProducts ?? false,
          qrCodeShare: features?.virtualTryOn?.qrCodeShare ?? false,
          buttonZIndex: features?.virtualTryOn?.buttonZIndex ?? 2,
          animatePdpButton: features?.virtualTryOn?.animatePdpButton ?? false,
          buttonImageQuerySelector:
            features?.virtualTryOn?.buttonImageQuerySelector ?? '',
          enableImageCropping:
            features?.virtualTryOn?.enableImageCropping ?? false,
          enablePreviousImagesCarousel:
            features?.virtualTryOn?.enablePreviousImagesCarousel ?? false,
          zoomToggle: features?.virtualTryOn?.zoomToggle ?? true,
          backgroundReplacement:
            features?.virtualTryOn?.backgroundReplacement ?? false,
          backgroundImage:
            features?.virtualTryOn?.backgroundImage ??
            `${getWidgetCdnPath()}/virtual-try-on/backgrounds/white.jpg`,
          watermark: features?.virtualTryOn?.watermark ?? false,
          watermarkImage:
            features?.virtualTryOn?.watermarkImage ??
            'https://widget.faslet.net/misc/faslet_logo.png',
          buttonPosition: features?.virtualTryOn?.buttonPosition ?? {
            bottom: '12px',
            right: '12px'
          },
          [field]: value
        }
      });
    },
    [features, onChange]
  );

  return (
    <>
      {(featureSet === 'widget' || featureSet === 'all') && (
        <>
          {!hideTitle && (
            <FormSectionTitle>{t('widgetFeatureConfig')}</FormSectionTitle>
          )}
          <SwitchField
            label={t('buttonHidden')}
            highlighted={isNotNullOrUndefined(externallyChanged?.buttonHidden)}
            value={!!features?.buttonHidden}
            onChange={checked =>
              onChange({
                ...features,
                buttonHidden: checked
              })
            }
            name="buttonHidden"
          />
          <SwitchField
            label={t('skipLabelDetection')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.skipLabelDetection
            )}
            value={!!features?.skipLabelDetection}
            onChange={checked =>
              onChange({
                ...features,
                skipLabelDetection: checked
              })
            }
            name="skipLabelDetection"
          />
          <SwitchField
            label={t('automaticTagPrediction')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.automaticTagPrediction
            )}
            value={!!features?.automaticTagPrediction}
            onChange={checked =>
              onChange({
                ...features,
                automaticTagPrediction: checked
              })
            }
            name="automaticTagPrediction"
          />
          {features.automaticTagPrediction && (
            <SwitchField
              label={t('automaticTagOnlySizeUploads')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.automaticTagOnlySizeUploads
              )}
              value={!!features?.automaticTagOnlySizeUploads}
              onChange={checked =>
                onChange({
                  ...features,
                  automaticTagOnlySizeUploads: checked
                })
              }
              name="automaticTagOnlySizeUploads"
            />
          )}
          {features.automaticTagPrediction && (
            <SwitchField
              label={t('automaticallyAssociateBrands')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.automaticallyAssociateBrands
              )}
              value={!!features?.automaticallyAssociateBrands}
              onChange={checked =>
                onChange({
                  ...features,
                  automaticallyAssociateBrands: checked
                })
              }
              name="automaticallyAssociateBrands"
            />
          )}
          <SwitchField
            label={t('filledWidgetResult')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.filledWidgetResult
            )}
            value={!!features?.filledWidgetResult}
            onChange={checked =>
              onChange({
                ...features,
                filledWidgetResult: checked
              })
            }
            name="filledWidgetResult"
          />
          <SwitchField
            label={t('dualWidgetResults')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.dualWidgetResults
            )}
            value={!!features?.dualWidgetResults}
            onChange={checked =>
              onChange({
                ...features,
                dualWidgetResults: checked
              })
            }
            name="dualWidgetResults"
          />
          <SwitchField
            label={t('expandedWidgetResults')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.expandedWidgetResults
            )}
            value={!!features?.expandedWidgetResults}
            onChange={checked =>
              onChange({
                ...features,
                expandedWidgetResults: checked
              })
            }
            name="expandedWidgetResults"
          />
          <SwitchField
            label={t('widgetUserName')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.widgetUserName
            )}
            value={!!features?.widgetUserName}
            onChange={checked =>
              onChange({
                ...features,
                widgetUserName: checked
              })
            }
            name="widgetUserName"
          />
          <SwitchField
            label={t('mobileHeightAndWeightInputField')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.mobileHeightAndWeightInputField
            )}
            value={!!features?.mobileHeightAndWeightInputField}
            onChange={checked =>
              onChange({
                ...features,
                mobileHeightAndWeightInputField: checked
              })
            }
            name="mobileHeightAndWeightInputField"
          />
          <SwitchField
            label={t('mobileHeightAndWeightSlotMachinePicker')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.mobileHeightAndWeightSlotMachinePicker
            )}
            value={!!features?.mobileHeightAndWeightSlotMachinePicker}
            onChange={checked =>
              onChange({
                ...features,
                mobileHeightAndWeightSlotMachinePicker: checked
              })
            }
            name="mobileHeightAndWeightSlotMachinePicker"
          />
          <SwitchField
            label={t('ageAsDropdown')}
            highlighted={isNotNullOrUndefined(externallyChanged?.ageAsDropdown)}
            value={!!(features?.ageAsDropdown ?? true)}
            onChange={checked =>
              onChange({
                ...features,
                ageAsDropdown: checked
              })
            }
            name="ageAsDropdown"
          />
          <SwitchField
            label={t('preciseFitSteps')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.preciseFitSteps
            )}
            value={!!(features?.preciseFitSteps ?? false)}
            onChange={checked =>
              onChange({
                ...features,
                preciseFitSteps: checked
              })
            }
            name="preciseFitSteps"
          />
          <SwitchField
            label={t('customerFeedback')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.customerFeedback
            )}
            value={!!features?.customerFeedback}
            onChange={checked =>
              onChange({
                ...features,
                customerFeedback: checked
              })
            }
            name="customerFeedback"
          />
          <SwitchField
            label={t('braSizeInsteadOfChest')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.braSizeInsteadOfChest
            )}
            value={!!features?.braSizeInsteadOfChest}
            onChange={checked =>
              onChange({
                ...features,
                braSizeInsteadOfChest: checked
              })
            }
            name="braSizeInsteadOfChest"
          />
          <SwitchField
            label={t('orderBasedSizing')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.orderBasedSizing
            )}
            value={!!features?.orderBasedSizing}
            onChange={checked =>
              onChange({
                ...features,
                orderBasedSizing: checked
              })
            }
            name="orderBasedSizing"
          />
          <SwitchField
            label={t('remapSizeResultsForProfileBuckets')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.remapSizeResultsForProfileBuckets
            )}
            value={!!features?.remapSizeResultsForProfileBuckets}
            onChange={checked =>
              onChange({
                ...features,
                remapSizeResultsForProfileBuckets: checked
              })
            }
            name="remapSizeResultsForProfileBuckets"
          />
          <SwitchField
            label={t('disableSizeChartReturnDeltasForRemappedSizes')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.disableSizeChartReturnDeltasForRemappedSizes
            )}
            value={!!features?.disableSizeChartReturnDeltasForRemappedSizes}
            onChange={checked =>
              onChange({
                ...features,
                disableSizeChartReturnDeltasForRemappedSizes: checked
              })
            }
            name="disableSizeChartReturnDeltasForRemappedSizes"
          />
          <SwitchField
            label={t('excludeGlobalReferenceBrands')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.excludeGlobalReferenceBrands
            )}
            value={features?.excludeGlobalReferenceBrands ?? false}
            onChange={checked =>
              onChange({
                ...features,
                excludeGlobalReferenceBrands: checked
              })
            }
            name="excludeGlobalReferenceBrandsEnabled"
          />
          <SwitchField
            label={t('uiRefresh2024')}
            highlighted={isNotNullOrUndefined(externallyChanged?.uiRefresh2024)}
            value={!!features?.uiRefresh2024}
            onChange={checked =>
              onChange({
                ...features,
                uiRefresh2024: checked
              })
            }
            name="uiRefresh2024"
          />
          <FormSubsection hideBorder={!features?.virtualTryOn?.enabled}>
            <SwitchField
              label={t('virtualTryOn')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.virtualTryOn?.enabled
              )}
              value={!!features?.virtualTryOn?.enabled}
              onChange={checked => updateVirtualTryOn('enabled', checked)}
              name="virtualTryOn"
            />
            <Collapse in={features?.virtualTryOn?.enabled}>
              <FormSection>
                <TextField
                  label={t('virtualTryOnButtonImageQuerySelector')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.buttonImageQuerySelector
                  )}
                  value={features?.virtualTryOn?.buttonImageQuerySelector ?? ''}
                  onChange={(value?: string) =>
                    updateVirtualTryOn('buttonImageQuerySelector', value || '')
                  }
                  placeholder=".product-image img"
                />
                <SwitchField
                  label={t('virtualTryOnCustomPdpButton')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.customPdpButton
                  )}
                  value={!!features?.virtualTryOn?.customPdpButton}
                  onChange={checked =>
                    updateVirtualTryOn('customPdpButton', checked)
                  }
                  name="virtualTryOnCustomPdpButton"
                />
                <SwitchField
                  label={t('virtualTryOnPdpImageTakeover')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.pdpImageTakeover
                  )}
                  value={features?.virtualTryOn?.pdpImageTakeover ?? true}
                  onChange={checked =>
                    updateVirtualTryOn('pdpImageTakeover', checked)
                  }
                  name="virtualTryOnPdpImageTakeover"
                />
                <SelectField
                  label={t('virtualTryOnModelName')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.modelName
                  )}
                  value={features?.virtualTryOn?.modelName || 'tryon-v1.6'}
                  options={[
                    {
                      value: 'tryon-v1.5',
                      label: t('virtualTryOnModelNameV15')
                    },
                    {
                      value: 'tryon-v1.6',
                      label: t('virtualTryOnModelNameV16')
                    }
                  ]}
                  onChange={value =>
                    updateVirtualTryOn('modelName', value as string)
                  }
                />
                <SelectField
                  label={t('virtualTryOnMode')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.mode
                  )}
                  value={features?.virtualTryOn?.mode || 'balanced'}
                  options={[
                    { value: 'balanced', label: t('virtualTryOnModeBalanced') },
                    {
                      value: 'performance',
                      label: t('virtualTryOnModePerformance')
                    },
                    { value: 'quality', label: t('virtualTryOnModeQuality') }
                  ]}
                  onChange={value =>
                    updateVirtualTryOn('mode', value as string)
                  }
                />
                <NumberField
                  label={t('virtualTryOnLimitMonthly')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.limitMonthly
                  )}
                  value={features?.virtualTryOn?.limitMonthly ?? 1000}
                  onChange={(value: number) =>
                    updateVirtualTryOn('limitMonthly', value)
                  }
                />
                <SelectField
                  label={t('targetDevice')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.targetDevice
                  )}
                  value={features?.virtualTryOn?.targetDevice || 'all'}
                  options={[
                    { value: 'all', label: t('all') },
                    { value: 'mobile', label: t('mobile') },
                    { value: 'desktopTablet', label: t('desktopTablet') }
                  ]}
                  onChange={value =>
                    updateVirtualTryOn(
                      'targetDevice',
                      value as 'all' | 'mobile' | 'desktopTablet'
                    )
                  }
                />
                <SwitchField
                  label={t('onlyOnSelectedProducts')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.onlyOnSelectedProducts
                  )}
                  value={!!features?.virtualTryOn?.onlyOnSelectedProducts}
                  onChange={checked =>
                    updateVirtualTryOn('onlyOnSelectedProducts', checked)
                  }
                  name="virtualTryOnOnlyOnSelectedProducts"
                />
                <SwitchField
                  label={t('qrCodeShare')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.qrCodeShare
                  )}
                  value={!!features?.virtualTryOn?.qrCodeShare}
                  onChange={checked =>
                    updateVirtualTryOn('qrCodeShare', checked)
                  }
                  name="virtualTryOnQrCodeShare"
                />
                <NumberField
                  label={t('buttonZIndex')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.buttonZIndex
                  )}
                  value={features?.virtualTryOn?.buttonZIndex ?? 2}
                  onChange={(value: number) =>
                    updateVirtualTryOn('buttonZIndex', value)
                  }
                />
                <FormRow
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.buttonPosition
                  )}
                >
                  <FormLabel>{t('buttonPosition')}</FormLabel>
                  <FormFieldWrapper>
                    <ButtonPositionEditor
                      value={
                        features?.virtualTryOn?.buttonPosition ?? {
                          bottom: '12px',
                          right: '12px'
                        }
                      }
                      onChange={position =>
                        updateVirtualTryOn('buttonPosition', position)
                      }
                    />
                  </FormFieldWrapper>
                </FormRow>
                <SwitchField
                  label={t('animatePdpButton')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.animatePdpButton
                  )}
                  value={!!features?.virtualTryOn?.animatePdpButton}
                  onChange={checked =>
                    updateVirtualTryOn('animatePdpButton', checked)
                  }
                  name="virtualTryOnAnimatePdpButton"
                />
                <SwitchField
                  label={t('enableImageCropping')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.enableImageCropping
                  )}
                  value={!!features?.virtualTryOn?.enableImageCropping}
                  onChange={checked =>
                    updateVirtualTryOn('enableImageCropping', checked)
                  }
                  name="virtualTryOnEnableImageCropping"
                />
                <SwitchField
                  label={t('enablePreviousImagesCarousel')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn
                      ?.enablePreviousImagesCarousel
                  )}
                  value={!!features?.virtualTryOn?.enablePreviousImagesCarousel}
                  onChange={checked =>
                    updateVirtualTryOn('enablePreviousImagesCarousel', checked)
                  }
                  name="virtualTryOnEnablePreviousImagesCarousel"
                />
                <SwitchField
                  label={t('zoomToggle')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.zoomToggle
                  )}
                  value={features?.virtualTryOn?.zoomToggle ?? true}
                  onChange={checked =>
                    updateVirtualTryOn('zoomToggle', checked)
                  }
                  name="virtualTryOnZoomToggle"
                />
                <SwitchField
                  label={t('backgroundReplacement')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.backgroundReplacement
                  )}
                  value={!!features?.virtualTryOn?.backgroundReplacement}
                  onChange={checked =>
                    updateVirtualTryOn('backgroundReplacement', checked)
                  }
                  name="virtualTryOnBackgroundReplacement"
                />
                <Collapse in={features?.virtualTryOn?.backgroundReplacement}>
                  <FormSection>
                    <FormRow
                      highlighted={isNotNullOrUndefined(
                        externallyChanged?.virtualTryOn?.backgroundImage
                      )}
                    >
                      <FormLabel>{t('backgroundImage')}</FormLabel>
                      <FormFieldWrapper>
                        <BackgroundImageField
                          highlighted={isNotNullOrUndefined(
                            externallyChanged?.virtualTryOn?.backgroundImage
                          )}
                          value={features?.virtualTryOn?.backgroundImage}
                          onChange={(value?: string) =>
                            updateVirtualTryOn('backgroundImage', value || '')
                          }
                          retailerSlug={retailerSlug}
                        />
                      </FormFieldWrapper>
                    </FormRow>
                  </FormSection>
                </Collapse>
                <SwitchField
                  label={t('watermark')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.virtualTryOn?.watermark
                  )}
                  value={!!features?.virtualTryOn?.watermark}
                  onChange={checked => updateVirtualTryOn('watermark', checked)}
                  name="virtualTryOnWatermark"
                />
                <Collapse in={features?.virtualTryOn?.watermark}>
                  <FormSection>
                    <FormRow
                      highlighted={isNotNullOrUndefined(
                        externallyChanged?.virtualTryOn?.watermarkImage
                      )}
                    >
                      <FormLabel>{t('watermarkImage')}</FormLabel>
                      <FormFieldWrapper>
                        <WatermarkImageField
                          highlighted={isNotNullOrUndefined(
                            externallyChanged?.virtualTryOn?.watermarkImage
                          )}
                          value={features?.virtualTryOn?.watermarkImage}
                          onChange={(value?: string) =>
                            updateVirtualTryOn('watermarkImage', value || '')
                          }
                          retailerSlug={retailerSlug}
                        />
                      </FormFieldWrapper>
                    </FormRow>
                  </FormSection>
                </Collapse>
              </FormSection>
            </Collapse>
          </FormSubsection>
          <FormSubsection hideBorder={!features?.fitBar?.enabled}>
            <SwitchField
              label={t('fitBar')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.fitBar?.enabled
              )}
              value={!!features?.fitBar?.enabled}
              onChange={checked => updateFitBar('enabled', checked)}
              name="fitBar"
            />
            <Collapse in={features?.fitBar?.enabled}>
              <FormSection>
                <NumberField
                  label={t('sizeChartReturnsThreshold')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.fitBar?.sizeChartReturnsThreshold
                  )}
                  value={features?.fitBar?.sizeChartReturnsThreshold ?? 50}
                  onChange={(value: number) =>
                    updateFitBar('sizeChartReturnsThreshold', value)
                  }
                />
                <SwitchField
                  label={t('fitBarOnlyWhenNotFilled')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.fitBar?.onlyWhenNotFilled
                  )}
                  value={!!features?.fitBar?.onlyWhenNotFilled}
                  onChange={checked =>
                    updateFitBar('onlyWhenNotFilled', checked)
                  }
                  name="fitBarOnlyWhenNotFilled"
                />
                <SwitchField
                  label={t('fitBarHideIfZero')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.fitBar?.hideIfZero
                  )}
                  value={!!features?.fitBar?.hideIfZero}
                  onChange={checked => updateFitBar('hideIfZero', checked)}
                  name="fitBarHideIfZero"
                />
                <SwitchField
                  label={t('fitBarShowIfUndefined')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.fitBar?.showIfUndefined
                  )}
                  value={!!features?.fitBar?.showIfUndefined}
                  onChange={checked => updateFitBar('showIfUndefined', checked)}
                  name="fitBarShowIfUndefined"
                />
                <SwitchField
                  label={t('customFitBarVisuals')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.fitBar?.customFitBarVisuals
                  )}
                  value={!!features?.fitBar?.customFitBarVisuals}
                  onChange={checked =>
                    updateFitBar('customFitBarVisuals', checked)
                  }
                  name="customFitBarVisuals"
                />
                <SwitchField
                  label={t('usePerProductTtsIfSet')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.fitBar?.usePerProductTtsIfSet
                  )}
                  value={!!features?.fitBar?.usePerProductTtsIfSet}
                  onChange={checked =>
                    updateFitBar('usePerProductTtsIfSet', checked)
                  }
                  name="usePerProductTtsIfSet"
                />
              </FormSection>
            </Collapse>
          </FormSubsection>
          <FormSubsection
            hideBorder={!features?.productRecommendations?.enabled}
          >
            <SwitchField
              label={t('productRecommendations')}
              highlighted={isNotNullOrUndefined(
                externallyChanged?.productRecommendations?.enabled
              )}
              value={!!features?.productRecommendations?.enabled}
              onChange={checked =>
                updateProductRecommendations('enabled', checked)
              }
              name="productRecommendations"
            />
            <Collapse in={features?.productRecommendations?.enabled}>
              <FormSection>
                <SwitchField
                  label={t('productRecommendationsOnlyOutOfStock')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.productRecommendations?.onlyOutOfStock
                  )}
                  value={!!features?.productRecommendations?.onlyOutOfStock}
                  onChange={checked =>
                    updateProductRecommendations('onlyOutOfStock', checked)
                  }
                  name="productRecommendationsOnlyOutOfStock"
                />
                <SwitchField
                  label={t('productRecommendationsMatchColor')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.productRecommendations?.matchColor
                  )}
                  value={!!features?.productRecommendations?.matchColor}
                  onChange={checked =>
                    updateProductRecommendations('matchColor', checked)
                  }
                  name="productRecommendationsMatchColor"
                />
                <SwitchField
                  label={t('productRecommendationsMatchGender')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.productRecommendations?.matchGender
                  )}
                  value={!!features?.productRecommendations?.matchGender}
                  onChange={checked =>
                    updateProductRecommendations('matchGender', checked)
                  }
                  name="productRecommendationsMatchGender"
                />
                <SwitchField
                  label={t('productRecommendationsMatchProductCategory')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.productRecommendations
                      ?.matchProductCategory
                  )}
                  value={
                    !!features?.productRecommendations?.matchProductCategory
                  }
                  onChange={checked =>
                    updateProductRecommendations(
                      'matchProductCategory',
                      checked
                    )
                  }
                  name="productRecommendationsMatchProductCategory"
                />
                <SwitchField
                  label={t('productRecommendationsMatchCustomLabel0')}
                  highlighted={isNotNullOrUndefined(
                    externallyChanged?.productRecommendations?.matchCustomLabel0
                  )}
                  value={!!features?.productRecommendations?.matchCustomLabel0}
                  onChange={checked =>
                    updateProductRecommendations('matchCustomLabel0', checked)
                  }
                  name="productRecommendationsMatchCustomLabel0"
                />
              </FormSection>
            </Collapse>
          </FormSubsection>
          <SwitchField
            label={t('showExplanation')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.showExplanation
            )}
            value={!!features?.showExplanation}
            onChange={checked =>
              onChange({
                ...features,
                showExplanation: checked
              })
            }
            name="showExplanation"
          />

          <SwitchField
            label={t('showMeasurementsWithResult')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.showMeasurementsWithResult
            )}
            value={!!features?.showMeasurementsWithResult}
            onChange={checked =>
              onChange({
                ...features,
                showMeasurementsWithResult: checked
              })
            }
            name="showMeasurementsWithResult"
          />
        </>
      )}
      {(featureSet === 'portal' || featureSet === 'all') && (
        <>
          {!hideTitle && (
            <FormSectionTitle>{t('portalFeatureConfig')}</FormSectionTitle>
          )}
          <SwitchField
            label={t('widgetTester')}
            highlighted={isNotNullOrUndefined(externallyChanged?.widgetTester)}
            value={features?.widgetTester ?? true}
            onChange={checked =>
              onChange({
                ...features,
                widgetTester: checked
              })
            }
            name="widgetTester"
          />
          <SwitchField
            label={t('effects')}
            highlighted={isNotNullOrUndefined(externallyChanged?.effects)}
            value={features?.effects ?? true}
            onChange={checked =>
              onChange({
                ...features,
                effects: checked
              })
            }
            name="effectsEnabled"
          />
          <SwitchField
            label={t('integrations')}
            highlighted={isNotNullOrUndefined(externallyChanged?.integrations)}
            value={features?.integrations ?? false}
            onChange={checked =>
              onChange({
                ...features,
                integrations: checked
              })
            }
            name="integrationsEnabled"
          />
          <SwitchField
            label={t('returnInsights')}
            highlighted={isNotNullOrUndefined(externallyChanged?.returns)}
            value={features?.returns ?? false}
            onChange={checked =>
              onChange({
                ...features,
                returns: checked
              })
            }
            name="returnsEnabled"
          />
          <SwitchField
            label={t('profileInsights')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.profileInsights
            )}
            value={features?.profileInsights ?? false}
            onChange={checked =>
              onChange({
                ...features,
                profileInsights: checked
              })
            }
            name="profileInsightsEnabled"
          />
          <SwitchField
            label={t('productInsights')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.productInsights
            )}
            value={features?.productInsights ?? false}
            onChange={checked =>
              onChange({
                ...features,
                productInsights: checked
              })
            }
            name="productInsightsEnabled"
          />
          <SwitchField
            label={t('missedSalesInsights')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.missedSalesInsights
            )}
            value={features?.missedSalesInsights ?? false}
            onChange={checked =>
              onChange({
                ...features,
                missedSalesInsights: checked
              })
            }
            name="missedSalesInsightsEnabled"
          />
          <SwitchField
            label={t('productsScreen')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.productsScreen
            )}
            value={features?.productsScreen ?? false}
            onChange={checked =>
              onChange({
                ...features,
                productsScreen: checked
              })
            }
            name="productScreenEnabled"
          />
          <SwitchField
            label={t('ttsEditorInProductsScreen')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.ttsEditorInProductsScreen
            )}
            value={features?.ttsEditorInProductsScreen ?? false}
            onChange={checked =>
              onChange({
                ...features,
                ttsEditorInProductsScreen: checked
              })
            }
            name="ttsEditorInProductsScreenEnabled"
          />
          <SwitchField
            label={t('feedOnlyProductsInProductScreen')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.feedOnlyProductsInProductScreen
            )}
            value={features?.feedOnlyProductsInProductScreen ?? false}
            onChange={checked =>
              onChange({
                ...features,
                feedOnlyProductsInProductScreen: checked
              })
            }
            name="feedOnlyProductsInProductScreenEnabled"
          />
          <SwitchField
            label={t('roiTile')}
            highlighted={isNotNullOrUndefined(externallyChanged?.roiTile)}
            value={features?.roiTile ?? false}
            onChange={checked =>
              onChange({
                ...features,
                roiTile: checked
              })
            }
            name="roiTileEnabled"
          />
          <SwitchField
            label={t('repeatReturners')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.repeatReturners
            )}
            value={features?.repeatReturners ?? false}
            onChange={checked =>
              onChange({
                ...features,
                repeatReturners: checked
              })
            }
            name="repeatReturnersEnabled"
          />
          <SwitchField
            label={t('localizationEditor')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.localizationEditor
            )}
            value={features?.localizationEditor ?? false}
            onChange={checked =>
              onChange({
                ...features,
                localizationEditor: checked
              })
            }
            name="localizationEditorEnabled"
          />
          <SwitchField
            label={t('languageChooser')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.languageChoice
            )}
            value={features?.languageChoice ?? false}
            onChange={checked =>
              onChange({
                ...features,
                languageChoice: checked
              })
            }
            name="languageChooserEnabled"
          />
          <SwitchField
            label={t('dashboardSegmentation')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.dashboardSegmentation
            )}
            value={features?.dashboardSegmentation ?? false}
            onChange={checked =>
              onChange({
                ...features,
                dashboardSegmentation: checked
              })
            }
            name="dashboardSegmentationEnabled"
          />
          <SwitchField
            label={t('aiAnalytics')}
            highlighted={isNotNullOrUndefined(externallyChanged?.aiAnalytics)}
            value={features?.aiAnalytics ?? false}
            onChange={checked =>
              onChange({
                ...features,
                aiAnalytics: checked
              })
            }
            name="aiAnalyticsEnabled"
          />
          <SwitchField
            label={t('shopifyProductSync')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.shopifyProductSync
            )}
            value={features?.shopifyProductSync ?? false}
            onChange={checked =>
              onChange({
                ...features,
                shopifyProductSync: checked
              })
            }
            name="shopifyProductSyncEnabled"
          />
          <SwitchField
            label={t('shopifyReturnsSync')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.shopifyReturnsSync
            )}
            value={features?.shopifyReturnsSync ?? false}
            onChange={checked =>
              onChange({
                ...features,
                shopifyReturnsSync: checked
              })
            }
            name="shopifyReturnsSyncEnabled"
          />
          <SwitchField
            label={t('showMultipleSizesTile')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.showMultipleSizesTile
            )}
            value={features?.showMultipleSizesTile ?? false}
            onChange={checked =>
              onChange({
                ...features,
                showMultipleSizesTile: checked
              })
            }
            name="showMultipleSizesTile"
          />
          <SwitchField
            label={t('appendMetadataVariantCodeToReturnlessReturnSKU')}
            highlighted={isNotNullOrUndefined(
              externallyChanged?.appendMetadataVariantCodeToReturnlessReturnSKU
            )}
            value={!!features?.appendMetadataVariantCodeToReturnlessReturnSKU}
            onChange={checked =>
              onChange({
                ...features,
                appendMetadataVariantCodeToReturnlessReturnSKU: checked
              })
            }
            name="appendMetadataVariantCodeToReturnlessReturnSKU"
          />
          <FormSectionTitle>{t('aovFeatureConfig')}</FormSectionTitle>
          <SwitchField
            label={t('aov')}
            highlighted={isNotNullOrUndefined(externallyChanged?.aovDashboard)}
            value={features?.aovDashboard?.enabled}
            onChange={checked =>
              onChange({
                ...features,
                aovDashboard: {
                  enabled: checked,
                  currencySymbol: features?.aovDashboard?.currencySymbol ?? '€',
                  currencyMultiplier:
                    features?.aovDashboard?.currencyMultiplier ?? 1
                }
              })
            }
            name="aovDashboard"
          />
          <TextField
            label={t('currencySymbol')}
            value={features?.aovDashboard?.currencySymbol ?? '€'}
            onChange={(value?: string) =>
              onChange({
                ...features,
                aovDashboard: {
                  enabled: !!features?.aovDashboard?.enabled,
                  currencySymbol: value || '€',
                  currencyMultiplier:
                    features?.aovDashboard?.currencyMultiplier ?? 1
                }
              })
            }
          />
          <NumberField
            label={t('currencyMultiplier')}
            value={features?.aovDashboard?.currencyMultiplier ?? 1}
            onChange={(value: number) =>
              onChange({
                ...features,
                aovDashboard: {
                  enabled: !!features?.aovDashboard?.enabled,
                  currencySymbol: features?.aovDashboard?.currencySymbol ?? '€',
                  currencyMultiplier: value
                }
              })
            }
          />
        </>
      )}
    </>
  );
}
